// Переключатель темы: три положения, одно нажатие до любого.
//
// Стоит в полосе разделов рядом с выходом, а НЕ в «Настройках». Причина простая: раздел
// «Настройки» виден трём ролям из девяти — администратору, финотделу и руководству, — а тема
// это про монитор и освещение в комнате, а не про права на данные. Табельщик и кадровик
// сидят за такими же экранами.
//
// Выбор из трёх, а не переключатель из двух: «как в системе» — это не третья тема, а отказ
// выбирать. Человек один раз настроил Windows и ждёт, что программы это уважают.

import { useEffect, useState } from 'react';

import { RiCheckLine, RiComputerLine, RiMoonLine, RiSunLine } from '@remixicon/react';
import type { RemixiconComponentType } from '@remixicon/react';

import * as Popover from '@/shared/ui/popover';
import * as Tooltip from '@/shared/ui/tooltip';
import { cn } from '@/utils/cn';

import { type Тема, выбраннаяТема, выбратьТему, системнаяТёмная } from './index';

const ВАРИАНТЫ: readonly { тема: Тема; подпись: string; значок: RemixiconComponentType }[] = [
  { тема: 'система', подпись: 'Как в системе', значок: RiComputerLine },
  { тема: 'светлая', подпись: 'Светлая', значок: RiSunLine },
  { тема: 'тёмная', подпись: 'Тёмная', значок: RiMoonLine },
];

export function ThemeSwitch() {
  const [тема, setТема] = useState<Тема>(() => выбраннаяТема());
  const [тёмнаяВСистеме, setТёмнаяВСистеме] = useState<boolean>(() => системнаяТёмная());

  // Значок кнопки показывает, что сейчас НА ЭКРАНЕ, а не что выбрано в списке: при выборе
  // «как в системе» человеку важнее видеть текущее состояние, чем название режима.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return;
    }
    const наблюдение = window.matchMedia('(prefers-color-scheme: dark)');
    const обработчик = () => setТёмнаяВСистеме(наблюдение.matches);
    наблюдение.addEventListener('change', обработчик);
    return () => {
      наблюдение.removeEventListener('change', обработчик);
    };
  }, []);

  const сейчасТёмная = тема === 'тёмная' || (тема === 'система' && тёмнаяВСистеме);
  const Значок = сейчасТёмная ? RiMoonLine : RiSunLine;

  return (
    <Tooltip.Provider>
      <Popover.Root>
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <Popover.Trigger asChild>
              <button
                type='button'
                aria-label='Оформление'
                className='цель-тача relative flex size-8 shrink-0 items-center justify-center rounded-lg text-text-soft-400 transition duration-200 ease-out hover:bg-bg-weak-50 hover:text-text-strong-950'
              >
                <Значок className='size-5' />
              </button>
            </Popover.Trigger>
          </Tooltip.Trigger>
          <Tooltip.Content side='top'>Оформление</Tooltip.Content>
        </Tooltip.Root>

        <Popover.Content align='start' side='top' className='w-56 p-2'>
          <div className='flex flex-col'>
            {ВАРИАНТЫ.map((вариант) => (
              <button
                key={вариант.тема}
                type='button'
                onClick={() => {
                  выбратьТему(вариант.тема);
                  setТема(вариант.тема);
                }}
                className={cn(
                  'flex items-center gap-2.5 rounded-lg px-2 py-2 text-left transition duration-200 ease-out',
                  'text-paragraph-sm text-text-strong-950 hover:bg-bg-weak-50',
                )}
              >
                <вариант.значок className='size-4 shrink-0 text-text-soft-400' />
                <span className='flex-1'>{вариант.подпись}</span>
                {тема === вариант.тема && (
                  <RiCheckLine className='size-4 shrink-0 text-primary-base' />
                )}
              </button>
            ))}
          </div>
        </Popover.Content>
      </Popover.Root>
    </Tooltip.Provider>
  );
}
